const DEVICE_LABELS = {
    audio: 'microphone',
    video: 'camera',
    both: 'camera and microphone'
};

export function classifyDeviceError(error = {}) {
    const name = error.name || '';
    const message = error.message || '';

    if (message === 'Permission denied by system') {
        return 'system-permission';
    }

    if (name === 'NotAllowedError' || name === 'gum.permission_denied') {
        return 'browser-permission';
    }

    if (name === 'NotFoundError' || name === 'gum.not_found') {
        return 'not-found';
    }

    if (name === 'OverconstrainedError' || name === 'gum.constraint_failed') {
        return 'unavailable';
    }

    return 'restart-chrome';
}

export function buildDeviceTroubleshootingGuide(kind, error = {}, platform = {}) {
    const device = DEVICE_LABELS[kind] || DEVICE_LABELS.both;
    const category = classifyDeviceError(error);
    const restartChromeStep = platform.isMac
        ? 'Fully quit Chrome with <strong>Chrome → Quit Google Chrome</strong> or <strong>⌘Q</strong>.'
        : platform.isWindows
            ? 'Fully exit Chrome with <strong>Chrome menu → Exit</strong>.'
            : 'Fully quit every Chrome window and process.';

    if (category === 'browser-permission') {
        return {
            category,
            title: `Allow ${device} access`,
            summary: `Chrome is not allowing Kino to use your selected ${device}.`,
            steps: [
                'Click the site controls icon next to Chrome’s address bar.',
                `Set ${device === 'camera and microphone' ? 'Camera and Microphone' : device} to <strong>Allow</strong>.`,
                'Reload Kino and try the selected device again.',
                'If access is still blocked, fully restart Chrome.'
            ],
            note: 'Changing the permission does not always restart Chrome’s media service immediately.'
        };
    }

    if (category === 'system-permission') {
        if (platform.isMac) {
            return {
                category,
                title: `Allow Chrome to use your ${device}`,
                summary: `macOS is blocking Chrome from using the selected ${device}.`,
                steps: [
                    `Open <strong>System Settings → Privacy & Security → ${device === 'microphone' ? 'Microphone' : device === 'camera' ? 'Camera' : 'Camera and Microphone'}</strong>.`,
                    'Enable access for Google Chrome.',
                    'Quit Chrome completely with <strong>⌘Q</strong>.',
                    'Reopen Chrome, return to Kino, and try again.'
                ],
                note: 'Reloading only the Kino tab is not enough after changing a macOS privacy permission.'
            };
        }

        if (platform.isWindows) {
            return {
                category,
                title: `Allow Chrome to use your ${device}`,
                summary: `Windows is blocking Chrome from using the selected ${device}.`,
                steps: [
                    `Open <strong>Settings → Privacy & security → ${device === 'microphone' ? 'Microphone' : device === 'camera' ? 'Camera' : 'Camera and Microphone'}</strong>.`,
                    `Turn on ${device} access and access for desktop applications.`,
                    'Exit Chrome completely from the Chrome menu.',
                    'Reopen Chrome, return to Kino, and try again.'
                ],
                note: 'Reloading only the Kino tab is not enough after changing a Windows privacy permission.'
            };
        }

        return {
            category,
            title: `Allow Chrome to use your ${device}`,
            summary: `Your operating system is blocking Chrome from using the selected ${device}.`,
            steps: [
                `Open your system privacy settings for the ${device}.`,
                'Enable access for Google Chrome.',
                restartChromeStep,
                'Reopen Chrome, return to Kino, and try again.'
            ],
            note: 'Reloading only the Kino tab is not enough after changing a system privacy permission.'
        };
    }

    if (category === 'not-found') {
        return {
            category,
            title: `${device.charAt(0).toUpperCase() + device.slice(1)} not found`,
            summary: `Kino cannot find the selected ${device}.`,
            steps: [
                `Make sure the ${device} is connected, powered on, and detected by your computer.`,
                'Reconnect USB devices or disconnect and reconnect Bluetooth devices.',
                `Choose another ${device} from Kino’s device menu.`,
                'If the device still does not appear, fully restart Chrome.'
            ],
            note: ''
        };
    }

    if (category === 'unavailable') {
        return {
            category,
            title: `The selected ${device} is unavailable`,
            summary: `The previously selected ${device} may have been disconnected or renamed.`,
            steps: [
                `Open Kino’s ${device === 'microphone' ? 'Audio' : device === 'camera' ? 'Video' : 'Device Settings'} menu.`,
                `Select <strong>Default</strong> or another available ${device}.`,
                'Try the selected device again.',
                'If it remains unavailable, fully restart Chrome.'
            ],
            note: ''
        };
    }

    return {
        category,
        title: `Chrome couldn’t start your ${device}`,
        summary: `Another application may be using the selected ${device}, or Chrome’s media service may need to restart.`,
        steps: [
            `Close Zoom, Teams, Meet, recording applications, and other browser tabs using your ${device}.`,
            restartChromeStep,
            'Reopen Chrome, return to this Kino, and click <strong>Try again</strong>.',
            `If it still fails, reconnect or choose another ${device}.`
        ],
        note: 'Refreshing Kino is not the same as restarting Chrome. You can also enter <code>chrome://restart</code> in Chrome’s address bar to restart Chrome and normally restore your open tabs.'
    };
}

export default class DeviceTroubleshootingGuide {
    constructor(lobby, platform = {}) {
        this.lobby = lobby;
        this.platform = platform;
        this.errors = {
            audio: null,
            video: null
        };
        this.modal = null;

        this.inlineMessage = $('<span/>', {
            class: 'lobby-device-troubleshooting-message'
        });
        this.inlineTroubleshootButton = $('<button/>', {
            type: 'button',
            class: 'lobby-device-troubleshooting-link',
            html: 'Troubleshoot'
        }).on('click', () => this.show());
        this.inlineRetryButton = $('<button/>', {
            type: 'button',
            class: 'lobby-device-troubleshooting-link',
            html: 'Try again'
        }).on('click', () => this.retry());
        this.inlineAlert = $('<div/>', {
            class: 'lobby-device-troubleshooting-alert d-none',
            role: 'alert',
            html: [
                this.inlineMessage,
                $('<span/>', {
                    class: 'lobby-device-troubleshooting-actions',
                    html: [this.inlineTroubleshootButton, this.inlineRetryButton]
                })
            ]
        });
        this.previewAction = $('<button/>', {
            type: 'button',
            class: 'lobby-preview-troubleshooting-link d-none',
            html: 'Troubleshoot camera'
        }).on('click', () => this.show('video'));
    }

    setError(kind, error) {
        if (kind !== 'audio' && kind !== 'video') {
            return;
        }

        this.errors[kind] = {
            name: error && error.name ? error.name : '',
            message: error && error.message ? error.message : ''
        };
        this.refresh();
    }

    clearError(kind) {
        if (kind !== 'audio' && kind !== 'video') {
            return;
        }

        this.errors[kind] = null;
        this.refresh();
    }

    refresh() {
        const hasAudioError = Boolean(this.errors.audio);
        const hasVideoError = Boolean(this.errors.video);

        if (!hasAudioError && !hasVideoError) {
            this.inlineAlert.addClass('d-none');
            this.previewAction.addClass('d-none');
            return;
        }

        const message = hasAudioError && hasVideoError
            ? 'Camera and microphone unavailable.'
            : hasAudioError
                ? 'Microphone unavailable.'
                : 'Camera unavailable.';

        this.inlineMessage.html(message);
        this.inlineAlert.removeClass('d-none');
        this.previewAction.toggleClass('d-none', !hasVideoError);
    }

    buildSheetAction(kind) {
        if (!this.errors[kind]) {
            return '';
        }

        const device = DEVICE_LABELS[kind];
        return $('<button/>', {
            type: 'button',
            class: 'dropdown-item kino-device-footer-action dropdown-action lobby-device-troubleshooting-sheet-action',
            html: [`<i class="fas fa-tools mr-2"></i>`, `<span>Troubleshoot ${device}</span>`]
        }).on('click', event => {
            event.preventDefault();
            event.stopPropagation();
            this.lobby.hideLobbyBottomSheet();
            this.show(kind);
        });
    }

    defaultKind() {
        if (this.errors.audio && this.errors.video) {
            return 'both';
        }
        if (this.errors.audio) {
            return 'audio';
        }
        if (this.errors.video) {
            return 'video';
        }
        return null;
    }

    errorFor(kind) {
        if (kind === 'both') {
            const errors = [this.errors.audio, this.errors.video].filter(Boolean);
            return errors.find(error => classifyDeviceError(error) === 'restart-chrome') || errors[0] || {};
        }
        return this.errors[kind] || {};
    }

    show(kind = null) {
        const activeKind = kind || this.defaultKind();
        if (!activeKind) {
            return;
        }

        this.lobby.hideLobbyBottomSheet();
        const guide = buildDeviceTroubleshootingGuide(activeKind, this.errorFor(activeKind), this.platform);
        const modalId = 'device-troubleshooting-guide';
        const closeButton = $('<button/>', {
            type: 'button',
            class: 'close-button-modal-kino',
            'aria-label': 'Close',
            html: '<span aria-hidden="true">&times;</span>'
        }).on('click', () => this.modal.modal('hide'));
        const steps = $('<ol/>', {
            class: 'device-troubleshooting-steps',
            html: guide.steps.map(step => $('<li/>', { html: step }))
        });
        const copyLinkButton = $('<button/>', {
            type: 'button',
            class: 'btn btn-primary kino-btn-secondary',
            html: 'Copy Kino link'
        }).on('click', () => this.copyKinoLink(copyLinkButton));
        const chooseDeviceButton = $('<button/>', {
            type: 'button',
            class: 'btn btn-primary kino-btn-secondary',
            html: activeKind === 'both' ? 'Open Device Settings' : 'Choose another device'
        }).on('click', () => this.chooseDevice(activeKind));
        const retryButton = $('<button/>', {
            type: 'button',
            class: 'btn btn-primary kino-btn-primary',
            html: 'Try again'
        }).on('click', () => this.retry(activeKind));
        const modalHeader = $('<div/>', {
            class: 'modal-header modal-header-kino',
            html: [
                $('<h4/>', {
                    class: 'modal-title',
                    id: `${modalId}-title`,
                    html: guide.title
                }),
                closeButton
            ]
        });
        const modalBody = $('<div/>', {
            class: 'modal-body device-troubleshooting-body',
            html: [
                $('<p/>', { class: 'device-troubleshooting-summary', html: guide.summary }),
                steps,
                guide.note ? $('<div/>', {
                    class: 'device-troubleshooting-note',
                    html: guide.note
                }) : ''
            ]
        });
        const modalFooter = $('<div/>', {
            class: 'modal-footer device-troubleshooting-footer',
            html: [copyLinkButton, chooseDeviceButton, retryButton]
        });
        const modalContent = $('<div/>', {
            class: 'modal-content kino-enhanced-modal device-troubleshooting-modal-content',
            html: [modalHeader, modalBody, modalFooter]
        });
        const modalDialog = $('<div/>', {
            class: 'modal-dialog modal-dialog-centered device-troubleshooting-modal-dialog',
            role: 'document',
            html: modalContent
        });
        const currentModal = $('<div/>', {
            class: 'modal fade device-troubleshooting-modal',
            id: modalId,
            tabindex: '-1',
            role: 'dialog',
            'aria-modal': 'true',
            'aria-labelledby': `${modalId}-title`,
            'aria-hidden': 'true',
            html: modalDialog
        });

        if (this.modal) {
            this.modal.remove();
        }
        this.modal = currentModal;
        currentModal.on('hidden.bs.modal', () => {
            currentModal.remove();
            if (this.modal === currentModal) {
                this.modal = null;
            }
        });
        currentModal.on('shown.bs.modal', () => retryButton.focus());
        $('body').append(currentModal);
        currentModal.modal('show');
    }

    closeThen(action) {
        const currentModal = this.modal;
        if (!currentModal) {
            action();
            return;
        }

        currentModal.one('hidden.bs.modal', action);
        currentModal.modal('hide');
    }

    retry(kind = null) {
        const activeKind = kind || this.defaultKind();
        if (!activeKind) {
            return;
        }

        this.closeThen(() => {
            if (activeKind === 'audio' || activeKind === 'both') {
                this.lobby.tryMicrophoneDevice(this.lobby.micDeviceId || false);
            }
            if (activeKind === 'video' || activeKind === 'both') {
                this.lobby.tryCameraDevice(this.lobby.cameraDeviceId || false);
            }
        });
    }

    chooseDevice(kind) {
        this.closeThen(() => {
            if (kind === 'audio') {
                this.lobby.renderAudioMenu();
                this.lobby.showLobbyBottomSheet();
                return;
            }
            if (kind === 'video') {
                this.lobby.renderVideoMenu();
                this.lobby.showLobbyBottomSheet();
                return;
            }
            this.lobby.devicesSettings.renderVoiceSettingsModal();
        });
    }

    copyKinoLink(button) {
        navigator.clipboard.writeText(window.location.href).then(() => {
            button.html('Link copied');
            setTimeout(() => button.html('Copy Kino link'), 2000);
        }).catch(() => {
            button.html('Copy failed');
            setTimeout(() => button.html('Copy Kino link'), 2000);
        });
    }
}
